// frontend/src/pages/admin/AdminSupport.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supportApi } from '../../api/support';
import { FaSearch, FaFilter, FaCircle, FaEye, FaExclamationCircle, FaSpinner } from 'react-icons/fa';

function AdminSupport() {
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    filterData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, filterStatus, searchTerm]);

  const fetchTickets = async () => {
    try {
      const data = await supportApi.getAllTicketsAdmin();
      setTickets(data);
    } catch (err) {
      console.error("Error fetching tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let result = tickets;

    if (filterStatus !== 'all') {
      result = result.filter(t => t.status === filterStatus);
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(t => 
        t.ticket_id.toLowerCase().includes(lower) ||
        t.subject.toLowerCase().includes(lower) ||
        (t.user_email && t.user_email.toLowerCase().includes(lower)) ||
        (t.user_name && t.user_name.toLowerCase().includes(lower))
      );
    }

    setFilteredTickets(result);
  };

  const getStatusBadge = (status) => {
    const safeStatus = status || 'unknown';
    
    const styles = {
      open: "bg-green-100 text-green-800",
      in_progress: "bg-blue-100 text-blue-800",
      awaiting_customer: "bg-orange-100 text-orange-800",
      resolved: "bg-gray-100 text-gray-800",
      closed: "bg-gray-200 text-gray-600",
      unknown: "bg-gray-300 text-gray-700"
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[safeStatus] || 'bg-gray-100'}`}>
        <FaCircle size={6} className="mr-1.5" />
        {safeStatus.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') return <span className="text-red-600 font-bold flex items-center gap-1"><FaExclamationCircle /> High</span>;
    if (priority === 'medium') return <span className="text-yellow-600 font-medium">Medium</span>;
    return <span className="text-gray-500">Low</span>;
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Helpdesk...</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Support Helpdesk</h1>

      {/* FILTERS - Responsive Stack */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="flex items-center gap-2 w-full md:w-auto bg-gray-50 p-2 rounded border border-gray-200">
          <FaFilter className="text-gray-400 ml-2" />
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-transparent border-none focus:ring-0 text-sm w-full"
          >
            <option value="all">All Tickets</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="awaiting_customer">Awaiting Customer</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="relative w-full md:w-64">
          <input 
            type="text" 
            placeholder="Search ID, Subject, User..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-primary"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* TABLE - Horizontal Scroll Enabled */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[800px]">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-sm uppercase tracking-wider border-b border-gray-200">
                <th className="p-4">Ticket ID</th>
                <th className="p-4">Subject</th>
                <th className="p-4">User</th>
                <th className="p-4">Priority</th>
                <th className="p-4">Status</th>
                <th className="p-4">Last Updated</th>
                <th className="p-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan="7" className="p-8 text-center text-gray-500">No tickets found.</td>
                </tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 font-mono text-sm font-bold text-primary">
                      <Link to={`/admin/support/${ticket.id}`}>{ticket.ticket_id}</Link>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-800 truncate max-w-xs" title={ticket.subject}>{ticket.subject}</div>
                      <div className="text-xs text-gray-500">{ticket.category}</div>
                    </td>
                    <td className="p-4 text-sm">
                      <div className="font-bold">{ticket.user_name}</div>
                      <div className="text-xs text-gray-500">{ticket.user_email}</div>
                    </td>
                    <td className="p-4 text-sm">
                      {getPriorityBadge(ticket.priority)}
                    </td>
                    <td className="p-4">
                      {getStatusBadge(ticket.status)}
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      {new Date(ticket.last_updated_at).toLocaleString()}
                    </td>
                    <td className="p-4 text-right">
                      <Link 
                        to={`/admin/support/${ticket.id}`}
                        className="bg-blue-50 text-blue-600 p-2 rounded hover:bg-blue-100 inline-block"
                        title="View Ticket"
                      >
                        <FaEye />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminSupport;